/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import withStyles from '@material-ui/core/styles/withStyles'; // Add this import
import { CreateOutlined, EditLocationOutlined, DeleteOutlined } from '@material-ui/icons';
import axios from 'axios';
import classnames from 'classnames';
import { isEqual, isNumber, some } from 'lodash'; // Add isEqual here
import debounce from 'lodash/debounce';
import React, { useContext } from 'react';
import mapboxgl from 'mapbox-gl';
import { ScaleControl } from 'react-map-gl';
import Link from 'redux-first-router-link';
import { trackGtagEvent } from 'utils';

import {
  InfoControl,
  Map,
  MapControls,
  RecenterControl,
  Spinner,
  UserMenu,
  ZoomControl,
  checkAdminRole, // Add this import
} from '@marapp/earth-shared';

import {
  COMPANY_ABOUT_URL,
  MAP_API_URL,
  MAP_CDN_URL,
  MAP_MAPBOX_TOKEN,
  MAP_ADMIN_URL, // Add this import
} from '../../config';
import experienceIMG from '../../images/pins/experience-marker.svg';
import { ILayer, ILayerGroup } from '../../modules/layers/model';
import { IPopup } from '../../modules/map/selectors';
import ProfileService from '../../services/ProfileService';
import {
  // MAP_CUSTOM_ATTRIBUTION,
  APP_BASEMAPS,
  APP_TERMS_OF_USE,
  MAP_SHOW_DISCLAIMER,
  POWERED_BY_URL,
  POWERED_BY_PC_URL,
} from '../../theme';
import { Auth0Context } from '../../utils/contexts';
import {
  extractCoordinatesFromUrl,
  isValidUrlCoordinateGroup,
  IUrlCoordinates,
  layerIdFromGroup,
} from '../../utils/map';
import CustomMapControls from './controls';
import LayerManager from './layer-manager';
import Legend from './legend';
import MapDisclaimer from './disclaimer';
import Popup from './popup';
import ZoomControlPopup from './zoom-control-popup';
import { Editor, DrawPolygonMode, EditingMode } from 'react-map-gl-draw';

import ControlPanel from './control-panel';
import { styles, getEditHandleStyle, getFeatureStyle } from './style';

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import {
  processDroppedGeoJsonFile,
  cacheGeoJsonData,
  retrieveCachedGeoJson,
  isGeoJson,
  uploadGeoJsonAsPlace,
  validateCoordinates,
  convertMultiPolygonToPolygons,
  processGeoJsonForEditor,
} from '../../utils/geojsonUpload';

const CUSTOM_IMAGES = [{ id: 'experience-marker', src: experienceIMG }];

interface IMap {
  viewport?: { zoom: any };
  bounds?: {};
  popup: IPopup;
  classes: any;
  className: string;
  layerManagerBounds?: {};
  interactions?: {};
  mapStyle?: string;
  mapLabels?: boolean;
  mapRoads?: boolean;
  mapboxConfig?: {};
  setInitialUrlCoordinates?: (coordinates: IUrlCoordinates) => void;
  setMapViewport?: (data: any) => void;
  setMapInteractions?: (data: any) => void;
  setMapBounds?: (data: any) => void;
  selectedOpen?: boolean;
  showDisclaimer?: boolean;
  setShowDisclaimer?: (data: any) => void;
  t?: (text: string, opt?: any) => string;
  page?: string;
  activeInteractiveLayersIds?: any;
  activeInteractiveLayers?: any[];
  activeInteractiveLayer?: any;
  layerGroups?: ILayerGroup[];
  layerManagerLayers?: any[];
  _editorRef?: any;
  selectedFeatureIndex?: number | null;
  containerComponent?: any;
  disableDragAndDrop?: boolean; // Add this prop
}

interface IMapState {
  initialUrlCoordinates?: IUrlCoordinates;
  selectedFeatureIndex?: number | null;
  viewport?: {
    longitude: number;
    latitude: number;
    zoom: number;
  };
  mode?: any;
  lngLat: [number, number] | null; // Change to array format
  isDragOver?: boolean;
  droppedFeatures?: any[];
  dragCounter?: number;
  showSourcesPopup?: boolean;
  attributionText?: string;
}

function isLngLatTuple(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  );
}

class MapComponent extends React.Component<IMap, IMapState> {
  public static defaultProps = {
    bounds: {},
    mapLabels: true,
    mapRoads: true,
  };
  public onViewportChange = debounce((viewport) => {
    const { setMapViewport, mapStyle } = this.props;
    setMapViewport(viewport);

    // Update URL with viewport coordinates and basemap
    if (viewport && viewport.latitude && viewport.longitude && viewport.zoom) {
      const url = new URL(window.location.href);

      // Set the coordinate parameters as a single comma-separated value
      const coordinates = `${parseFloat(viewport.latitude.toFixed(6))},${parseFloat(
        viewport.longitude.toFixed(6)
      )},${Math.round(viewport.zoom)}`;

      // Remove existing coordinates parameter
      url.searchParams.delete('coordinates');

      // Set the basemap parameter if available
      if (mapStyle) {
        url.searchParams.set('basemap', mapStyle);
      }

      // Manually construct the URL to avoid encoding the coordinates
      let urlString = url.toString();
      const separator = urlString.includes('?') ? '&' : '?';
      urlString += `${separator}coordinates=${coordinates}`;

      // Update the URL without triggering a page reload
      window.history.replaceState({}, '', urlString);
    }
  }, 250);
  private map: any;
  private _editorRef: any;
  private sourcesPopupRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);
    this._editorRef = null;
    this.sourcesPopupRef = React.createRef();
    this.state = {
      viewport: {
        longitude: -91.874,
        latitude: 42.76,
        zoom: 12,
      },
      mode: null,
      selectedFeatureIndex: null,
      lngLat: [0, 0], // Initialize as array
      isDragOver: false,
      droppedFeatures: [],
      dragCounter: 0,
      showSourcesPopup: false,
      attributionText: '',
    };
  }

  _updateViewport = (viewport) => {
    this.setState({ viewport });
  };

  _onSelect = (options) => {
    this.setState({ selectedFeatureIndex: options && options.selectedFeatureIndex });
  };

  _onDelete = () => {
    const selectedIndex = this.state.selectedFeatureIndex;
    if (selectedIndex !== null && selectedIndex >= 0) {
      this._editorRef.deleteFeatures(selectedIndex);
    }
  };

  _onUpdate = ({ editType }) => {
    if (editType === 'addFeature') {
      this.setState({
        mode: new EditingMode(),
      });
    }
  };

  // Drag and drop handlers
  _onDragEnter = (e: React.DragEvent) => {
    // Exit early if drag and drop is disabled
    if (this.props.disableDragAndDrop) {
      return;
    }

    // Ignore drags from legend components
    if (this.isDragFromLegend(e) || !this.isDragWithFiles(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const dragCounter = this.state.dragCounter + 1;
    this.setState({
      dragCounter,
      isDragOver: dragCounter > 0,
    });
  };

  _onDragOver = (e: React.DragEvent) => {
    // Exit early if drag and drop is disabled
    if (this.props.disableDragAndDrop) {
      return;
    }

    // Ignore drags from legend components
    if (this.isDragFromLegend(e) || !this.isDragWithFiles(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  _onDragLeave = (e: React.DragEvent) => {
    // Exit early if drag and drop is disabled
    if (this.props.disableDragAndDrop) {
      return;
    }

    // Ignore drags from legend components
    if (this.isDragFromLegend(e) || !this.isDragWithFiles(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const dragCounter = this.state.dragCounter - 1;
    this.setState({
      dragCounter,
      isDragOver: dragCounter > 0,
    });
  };

  _onDrop = (e: React.DragEvent) => {
    // Only handle actual file drops
    if (this.isDragFromLegend(e) || e.dataTransfer.files.length === 0) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    this.setState({
      isDragOver: false,
      dragCounter: 0,
    });

    const files = Array.from(e.dataTransfer.files) as File[];
    const jsonFiles = files.filter(
      (file: File) =>
        file.type === 'application/json' ||
        file.name.toLowerCase().endsWith('.geojson') ||
        file.name.toLowerCase().endsWith('.json')
    );

    if (jsonFiles.length === 0) {
      alert('Please drop a GeoJSON file (.geojson or .json)');
      return;
    }

    jsonFiles.forEach((file) => this._processGeoJsonFile(file));
  };

  _processGeoJsonFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (typeof result === 'string') {
          const jsonData = JSON.parse(result);

          // Cache the data using utility function
          const { cacheKey, metadata } = cacheGeoJsonData(jsonData, file.name, file);

          if (isGeoJson(jsonData)) {
            // Add to map
            this._addGeoJsonToMap(jsonData, file.name);

            // Store cache info in state
            this.setState((prevState) => ({
              droppedFeatures: [
                ...prevState.droppedFeatures,
                {
                  geoJsonData: jsonData,
                  cacheInfo: { cacheKey, metadata },
                  originalFileName: file.name,
                  timestamp: new Date().toISOString(),
                },
              ],
            }));

            // Send to IOChat
            this._sendToIOChat({ cacheKey, metadata }, jsonData);
          } else {
            alert('File is not valid GeoJSON format.');
          }
        }
      } catch (error) {
        console.error('Error parsing JSON file:', error);
        alert("Error parsing JSON file. Please ensure it's valid JSON format.");
      }
    };
    reader.readAsText(file);
  };

  _cacheGeoJsonFile = async (geoJsonData, originalFileName = 'dropped-geojson') => {
    try {
      // Create a temporary file object for the utility function
      const geoJsonContent = JSON.stringify(geoJsonData, null, 2);
      const blob = new Blob([geoJsonContent], { type: 'application/json' });
      const file = new File([blob], originalFileName, { type: 'application/json' });

      const { cacheKey, metadata } = cacheGeoJsonData(geoJsonData, originalFileName, file);

      return {
        cacheKey,
        metadata,
        localUrl: URL.createObjectURL(blob),
      };
    } catch (error) {
      console.error('Error caching GeoJSON:', error);
      return null;
    }
  };

  _sendToIOChat = (cacheInfo, geoJsonData) => {
    if (!cacheInfo) {
      console.error('No cache info available for IOChat');
      return;
    }

    try {
      // Create a message for the IOChat component
      const chatMessage = {
        type: 'geojson-import',
        data: {
          cacheKey: cacheInfo.cacheKey,
          filename: cacheInfo.metadata.filename,
          timestamp: cacheInfo.metadata.timestamp,
          featureCount: cacheInfo.metadata.featureCount,
          size: cacheInfo.metadata.size,
          // Include a sample of the data for the AI to understand the structure
          sample: {
            type: geoJsonData.type,
            properties:
              geoJsonData.type === 'FeatureCollection' && geoJsonData.features?.length > 0
                ? geoJsonData.features[0].properties
                : geoJsonData.properties,
            geometryType:
              geoJsonData.type === 'FeatureCollection' && geoJsonData.features?.length > 0
                ? geoJsonData.features[0].geometry?.type
                : geoJsonData.geometry?.type,
          },
          // Instructions for the AI
          instructions:
            'A GeoJSON file has been imported to the map. The full data is cached and available for creating a new named Place. Please help the user save this as a named location.',
        },
      };

      console.log('Sending message to IOChat:', chatMessage);

      // Check if IOChat global method exists
      const windowAny = window as any;
      if (windowAny.IOChat && typeof windowAny.IOChat.receiveGeoJsonImport === 'function') {
        console.log('Using IOChat global method');
        windowAny.IOChat.receiveGeoJsonImport(chatMessage);
      } else {
        console.log('Using custom event dispatch');
        // Fix: Match the expected event structure
        window.dispatchEvent(
          new CustomEvent('geojson-import', {
            detail: chatMessage, // This is the key fix - wrap the entire message in detail
          })
        );
      }

      console.log('GeoJSON info sent to IOChat:', chatMessage);

      // Show user feedback
      alert(
        `GeoJSON cached and sent to IOChat assistant!\nFile: ${cacheInfo.metadata.filename}\nFeatures: ${cacheInfo.metadata.featureCount}`
      );
    } catch (error) {
      console.error('Error sending to IOChat:', error);
      alert('Error sending GeoJSON to chat assistant. Check console for details.');
    }
  };

  _addGeoJsonToMap = (geoJsonData, originalFileName = 'dropped-geojson') => {
    if (!geoJsonData || !geoJsonData.type) {
      alert('Invalid GeoJSON format');
      return;
    }

    console.log('=== GEOJSON ANALYSIS ===');
    console.log('GeoJSON Type:', geoJsonData.type);

    // Analyze based on type
    if (geoJsonData.type === 'FeatureCollection') {
      console.log('Number of features:', geoJsonData.features?.length || 0);

      if (geoJsonData.features && geoJsonData.features.length > 0) {
        geoJsonData.features.forEach((feature, index) => {
          console.log(`--- Feature ${index + 1} ---`);
          console.log('  Geometry type:', feature.geometry?.type);
          console.log('  Properties:', feature.properties);

          // Count coordinates/points
          if (feature.geometry?.coordinates) {
            const coords = feature.geometry.coordinates;
            if (feature.geometry.type === 'Polygon') {
              console.log('  Number of rings:', coords.length);
              coords.forEach((ring, ringIndex) => {
                console.log(`    Ring ${ringIndex + 1} points:`, ring.length);
              });
            } else if (feature.geometry.type === 'MultiPolygon') {
              console.log('  Number of polygons:', coords.length);
              coords.forEach((polygon, polyIndex) => {
                console.log(`    Polygon ${polyIndex + 1} rings:`, polygon.length);
                polygon.forEach((ring, ringIndex) => {
                  console.log(`      Ring ${ringIndex + 1} points:`, ring.length);
                });
              });
            }
          }
        });
      }
    } else if (geoJsonData.type === 'Feature') {
      console.log('Single feature detected');
      console.log('Geometry type:', geoJsonData.geometry?.type);
      console.log('Properties:', geoJsonData.properties);

      // Count coordinates/points
      if (geoJsonData.geometry?.coordinates) {
        const coords = geoJsonData.geometry.coordinates;
        if (geoJsonData.geometry.type === 'Polygon') {
          console.log('Number of rings:', coords.length);
          coords.forEach((ring, ringIndex) => {
            console.log(`  Ring ${ringIndex + 1} points:`, ring.length);
          });
        } else if (geoJsonData.geometry.type === 'MultiPolygon') {
          console.log('Number of polygons:', coords.length);
          coords.forEach((polygon, polyIndex) => {
            console.log(`  Polygon ${polyIndex + 1} rings:`, polygon.length);
            polygon.forEach((ring, ringIndex) => {
              console.log(`    Ring ${ringIndex + 1} points:`, ring.length);
            });
          });
        }
      }
    }

    console.log('Raw GeoJSON data:', geoJsonData);
    console.log('=== END ANALYSIS ===');

    try {
      // Add features to the editor
      if (this._editorRef) {
        // Use the utility function to process GeoJSON
        const featuresToProcess = processGeoJsonForEditor(geoJsonData);

        console.log(
          `Processing ${featuresToProcess.length} polygon features (after MultiPolygon conversion)`
        );

        // Now process all the polygon features
        featuresToProcess.forEach((feature, index) => {
          // Validate coordinates before adding
          if (validateCoordinates(feature.geometry.coordinates)) {
            console.log(`Adding polygon feature ${index + 1}:`, feature);

            // Try to add feature with proper error handling
            try {
              // Ensure the feature has the required structure
              const cleanFeature = {
                type: 'Feature',
                properties: feature.properties || {},
                geometry: {
                  type: 'Polygon', // Always Polygon now
                  coordinates: feature.geometry.coordinates,
                },
              };

              console.log('Clean polygon feature:', cleanFeature);
              this._editorRef.addFeatures(cleanFeature);
              console.log(`Successfully added polygon feature ${index + 1}`);
            } catch (featureError) {
              console.error(`Error adding polygon feature ${index + 1}:`, featureError);
              console.error('Feature that caused error:', feature);
            }
          } else {
            console.warn(
              `Skipping polygon feature ${index + 1} - invalid coordinates:`,
              feature.geometry.coordinates
            );
          }
        });
      } else {
        console.warn('Editor ref not available');
        alert('Map editor is not ready. Please try again.');
        return;
      }

      // Store dropped features in state for potential other uses
      this.setState((prevState) => ({
        droppedFeatures: [...prevState.droppedFeatures, geoJsonData],
      }));

      console.log('GeoJSON successfully added to map');
    } catch (error) {
      console.error('Error adding GeoJSON to map:', error);
      console.error('Error stack:', error.stack);
      alert(`Error adding GeoJSON to map: ${error.message}. Check console for details.`);
    }
  };

  public componentDidMount() {
    const { setInitialUrlCoordinates } = this.props;
    const initialUrlCoordinates = extractCoordinatesFromUrl();
    this.setState({
      initialUrlCoordinates,
    });
    if (some(initialUrlCoordinates, isNumber)) {
      setInitialUrlCoordinates(initialUrlCoordinates);
    }

    this.map?.on('render', this.handleLoadIndicator)?.on('idle', this.handleLoadIndicator);

    // Add click outside listener for sources popup
    document.addEventListener('mousedown', this._handleClickOutside);
  }

  public componentWillUnmount() {
    this.map?.off('render', this.handleLoadIndicator)?.off('idle', this.handleLoadIndicator);

    // Remove click outside listener
    document.removeEventListener('mousedown', this._handleClickOutside);
  }

  public componentDidUpdate(prevProps) {
    const { mapLabels, mapRoads, interactions, viewport, setMapViewport } = this.props;
    const {
      mapLabels: prevMapLabels,
      mapRoads: prevMapRoads,
      interactions: prevInteractions,
    } = prevProps;

    if (mapLabels !== prevMapLabels) {
      this.setLabels();
    }

    if (mapRoads !== prevMapRoads) {
      this.setRoads();
    }

    if (!isEqual(interactions, prevInteractions)) {
      Object.keys(interactions).forEach((k) => {
        const { data, geometry } = interactions[k];

        if (data && data.cluster) {
          const { zoom } = viewport;

          this.map.getSource(k).getClusterExpansionZoom(data.cluster_id, (err, newZoom) => {
            if (err) {
              return;
            }
            const { coordinates } = geometry;
            const difference = Math.abs(zoom - newZoom);

            setMapViewport({
              latitude: coordinates[1],
              longitude: coordinates[0],
              zoom: newZoom,
              transitionDuration: 400 + difference * 100,
            });
          });
        }
      });
    }
  }

  public onZoomChange = (zoom) => {
    const { viewport } = this.props;

    this.onViewportChange({
      ...viewport,
      zoom,
      transitionDuration: 500,
    });
  };

  public onRecenterChange = () => {
    const { bounds, setMapBounds } = this.props;

    setMapBounds({});

    requestAnimationFrame(() => {
      setMapBounds(bounds);
    });
  };

  public onStyleLoad = () => {
    this.setLabels();
    this.setRoads();

    // Add custom images
    this.setCustomImages();
  };

  public onReady = ({ map }) => {
    this.map = map;

    // Add attribution control with options
    // map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    // Listeners
    this.map?.on('style.load', this.onStyleLoad);
  };

  public onMouseMove = debounce((e) => {
    let lngLat = e.lngLat;

    // Convert to array format for performance
    if (isLngLatTuple(lngLat)) {
      this.setState({ lngLat });
    } else if (lngLat && typeof lngLat === 'object') {
      this.setState({
        lngLat: [lngLat.lng || lngLat.longitude || 0, lngLat.lat || lngLat.latitude || 0],
      });
    } else {
      this.setState({ lngLat: [0, 0] });
    }
  }, 25); // Keep the 50ms debounce

  public onClick = (e) => {
    const { setMapInteractions } = this.props;

    if (e.features && e.features.length && !e.target.classList.contains('mapbox-prevent-click')) {
      // No better way to do this
      const { features, lngLat } = e;
      setMapInteractions({ features, lngLat });
    } else {
      setMapInteractions({});
    }
  };

  public onTransformRequest = (url, resourceType) => {
    const { layerGroups } = this.props;

    // calculate where the tile would be on the CDN
    // pass this to the fetch in the header X-Cache-Check-CDN
    // and intercept in service worker to actually check
    // because we can't call async code here
    if (resourceType === 'Tile' && url.includes(MAP_API_URL) && MAP_CDN_URL) {
      const tileUrl = new URL(url);
      // get layerId
      const tilePath = tileUrl.pathname.replace('/api/tiles', '');
      const layerId = tilePath.split('/')[1];
      const layer = layerIdFromGroup(layerGroups, layerId);

      // If no layer found in layerGroups, log warning and continue without CDN
      if (!layer) {
        console.warn(`Tile request for unknown layer: ${layerId}`);
        return {
          url,
          headers: { Authorization: axios.defaults.headers.common.Authorization },
        };
      }

      if (layer.STACConfig) {
        // transform to CDN path
        const cdnPath = '/tile_'
          .concat(layer.STACConfig.collection)
          .concat(tileUrl.searchParams.get('v'))
          .concat(layer.STACConfig.asset)
          .concat(layer.STACConfig.startDate)
          .concat(layer.STACConfig.endDate)
          .concat('.png')
          .replace(/-/g, '');

        if (cdnPath) {
          const cdnUrl = MAP_CDN_URL.concat('/map-tiles').concat(tilePath).concat(cdnPath);
          return {
            url,
            headers: { 'X-Cache-Check-CDN': cdnUrl },
          };
        }
      } else {
        // Log warning instead of error - layer exists but doesn't have STACConfig
        // This is expected for non-STAC layers (e.g., vector tiles, custom sources)
        console.warn(
          `Layer ${layer.id} (${
            layer.name || 'unnamed'
          }) does not have STACConfig - using direct API request`
        );
      }
    }

    if (resourceType === 'Source' && url.includes(MAP_API_URL)) {
      return {
        url,
        headers: { Authorization: axios.defaults.headers.common.Authorization },
      };
    }

    return {
      url,
    };
  };

  public setLabels = () => {
    const LABELS_GROUP = ['label', 'labels'];
    const LABELS_IGNORE_GROUP = 'road-labels';
    const SYMBOLS_GROUP = ['symbol'];

    const { mapLabels, mapRoads } = this.props;
    const { layers, metadata } = this.map.getStyle();
    let labelLayers;

    if (metadata && 'mapbox:groups' in metadata) {
      /* Determine label layers from mapbox metadata */
      const groups = Object.keys(metadata['mapbox:groups']).filter((k) => {
        const { name } = metadata['mapbox:groups'][k];
        const labelGroups = LABELS_GROUP.map(
          (lgr) =>
            name.toLowerCase().includes(lgr) && !name.toLowerCase().includes(LABELS_IGNORE_GROUP)
        );
        return labelGroups.some((bool) => bool);
      });

      labelLayers = layers.filter((l) => {
        const { metadata } = l;
        if (!metadata) {
          return false;
        }

        const gr = metadata['mapbox:group'];
        return groups.includes(gr);
      });
    } else {
      /* Determine label layers from source-layer */
      labelLayers = layers.filter((l) => {
        const { 'source-layer': sourceLayer, type } = l;
        if (sourceLayer) {
          const hasLabels = LABELS_GROUP.map(
            (lgr) =>
              sourceLayer.toLowerCase().includes(lgr) &&
              !sourceLayer.toLowerCase().includes(LABELS_IGNORE_GROUP)
          );
          const hasSymbols = SYMBOLS_GROUP.map((sgr) => type.toLowerCase().includes(sgr));
          return hasLabels.concat(hasSymbols).some((bool) => bool);
        }
      });
    }

    labelLayers.forEach((l) => {
      const visibility = mapLabels ? 'visible' : 'none';
      this.map.setLayoutProperty(l.id, 'visibility', visibility);
      this.map.moveLayer(l.id); // set to top of hierarchy
    });

    this.setRoadLabels();
  };

  public setRoads = () => {
    const ROADS_GROUP = ['road', 'roads', 'bridges', 'tunnels', 'surface', 'surface-icons'];
    const ROADS_IGNORE_GROUP = 'road-labels';

    const { mapRoads } = this.props;
    const { layers, metadata } = this.map.getStyle();
    let roadLayers;

    if (metadata && 'mapbox:groups' in metadata) {
      /* Determine road layers from mapbox metadata */
      const groups = Object.keys(metadata['mapbox:groups']).filter((k) => {
        const { name } = metadata['mapbox:groups'][k];
        const roadGroups = ROADS_GROUP.map(
          (rgr) =>
            name.toLowerCase().includes(rgr) && !name.toLowerCase().includes(ROADS_IGNORE_GROUP)
        );
        return roadGroups.some((bool) => bool);
      });

      roadLayers = layers.filter((l) => {
        const { metadata } = l;
        if (!metadata) {
          return false;
        }
        const gr = metadata['mapbox:group'];
        return groups.includes(gr);
      });
    } else {
      /* Determine road layers from source-layer */
      roadLayers = layers.filter((l) => {
        const { 'source-layer': sourceLayer } = l;
        if (sourceLayer) {
          const hasRoads = ROADS_GROUP.map(
            (rgr) =>
              sourceLayer.toLowerCase().includes(rgr) &&
              !sourceLayer.toLowerCase().includes(ROADS_IGNORE_GROUP)
          );
          return hasRoads.some((bool) => bool);
        }
      });
    }

    roadLayers.forEach((l) => {
      const visibility = mapRoads ? 'visible' : 'none';
      this.map.setLayoutProperty(l.id, 'visibility', visibility);
      this.map.moveLayer(l.id); // set to top of hierarchy
      // BUG, these should be BELOW admin labels
    });
    this.setRoadLabels();
  };

  public setRoadLabels = () => {
    const ROAD_LABELS_GROUP = ['road-labels'];

    const { mapLabels, mapRoads } = this.props;
    const { layers, metadata } = this.map.getStyle();
    let roadLabelLayers;

    if (metadata && 'mapbox:groups' in metadata) {
      /* Determine label layers from mapbox metadata */
      const roadLabelGroups = Object.keys(metadata['mapbox:groups']).filter((k) => {
        const { name } = metadata['mapbox:groups'][k];
        const labelGroups = ROAD_LABELS_GROUP.map((lgr) => name.toLowerCase().includes(lgr));

        return labelGroups.some((bool) => bool);
      });

      roadLabelLayers = layers.filter((l) => {
        const { metadata } = l;
        if (!metadata) {
          return false;
        }

        const gr = metadata['mapbox:group'];
        return roadLabelGroups.includes(gr);
      });
    } else {
      /* Determine road label layers from source-layer */
      roadLabelLayers = layers.filter((l) => {
        const { 'source-layer': sourceLayer } = l;
        if (sourceLayer) {
          const hasRoads = ROAD_LABELS_GROUP.map((rgr) =>
            sourceLayer.toLowerCase().includes(ROAD_LABELS_GROUP)
          );
          return hasRoads.some((bool) => bool);
        }
      });
    }

    // if roads are on, set labels visible
    roadLabelLayers.forEach((l) => {
      const visibility = mapRoads && mapLabels ? 'visible' : 'none';
      this.map.setLayoutProperty(l.id, 'visibility', visibility);
      this.map.moveLayer(l.id); // set to top of hierarchy
    });
  };

  public setCustomImages = () => {
    CUSTOM_IMAGES.forEach(({ id, src }) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        this.map.addImage(id, img);
      };
    });
  };

  /**
   * Once the map is loaded, check if there were any valid coordinates provided in the URL.
   * If so, set the viewport in accordance with those coordinates
   */
  public onLoad = () => {
    const { initialUrlCoordinates } = this.state;

    if (isValidUrlCoordinateGroup(initialUrlCoordinates)) {
      this.onViewportChange({
        ...initialUrlCoordinates,
        transitionDuration: 800,
      });

      this.onViewportChange.flush(); // execute directly the last call

      this.map.resize(); // trigger map resize to ensure layers load
    }
  };

  _getAttributionText = () => {
    if (this.map) {
      const attribElement = document.querySelector('.mapboxgl-ctrl-attrib-inner');
      if (attribElement) {
        return attribElement.innerHTML;
      }
    }
    return '';
  };

  _toggleSourcesPopup = () => {
    const attributionText = this._getAttributionText();
    this.setState((prevState) => ({
      showSourcesPopup: !prevState.showSourcesPopup,
      attributionText,
    }));
  };

  _closeSourcesPopup = () => {
    this.setState({ showSourcesPopup: false });
  };

  // Add click-outside handler method (after _closeSourcesPopup around line 878):
  _handleClickOutside = (event: MouseEvent) => {
    if (
      this.state.showSourcesPopup &&
      this.sourcesPopupRef.current &&
      !this.sourcesPopupRef.current.contains(event.target as Node)
    ) {
      this._closeSourcesPopup();
    }
  };

  public render() {
    const {
      selectedOpen,
      mapStyle,
      viewport,
      bounds,
      classes,
      className,
      popup,
      mapboxConfig,
      page,
      activeInteractiveLayersIds,
      activeInteractiveLayers,
      activeInteractiveLayer,
      layerGroups,
      layerManagerLayers,
      layerManagerBounds,
      setMapInteractions,
      showDisclaimer,
      setShowDisclaimer,
      t,
      router, // Add router prop
    } = this.props;

    const { lngLat, isDragOver, showSourcesPopup, attributionText } = this.state;

    // @ts-ignore
    return (
      <div
        className={classnames(classes.root, className, {
          [classes.open]: selectedOpen,
        })}
        onDragEnter={this._onDragEnter}
        onDragOver={this._onDragOver}
        onDragLeave={this._onDragLeave}
        onDrop={this._onDrop}
        style={{
          position: 'relative',
          ...(isDragOver && {
            backgroundColor: 'rgba(0, 123, 255, 0.1)',
            border: '2px dashed #007bff',
            borderRadius: '8px',
          }),
        }}
      >
        {isDragOver && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: '8px',
              border: '2px dashed #007bff',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#007bff',
              }}
            >
              Drop GeoJSON file here to add it to the map
            </div>
          </div>
        )}
        <UserMenuWrapper selected={page} />

        <Map
          mapboxApiAccessToken={MAP_MAPBOX_TOKEN}
          // Attributes
          mapStyle={APP_BASEMAPS.find((x) => x.slug === mapStyle)?.id}
          // customAttribution={MAP_CUSTOM_ATTRIBUTION}
          viewport={viewport}
          bounds={bounds}
          {...mapboxConfig}
          interactiveLayerIds={activeInteractiveLayersIds}
          // Functions
          onViewportChange={this.onViewportChange}
          onMouseMove={this.onMouseMove} // Listen for mouse movement
          onClick={this.onClick}
          onLoad={this.onLoad}
          onReady={this.onReady}
          transformRequest={this.onTransformRequest}
        >
          {(map) => {
            return (
              <div className="mapboxgl-ctrl-parent">
                <Popup
                  popup={popup}
                  setMapInteractions={setMapInteractions}
                  activeInteractiveLayers={activeInteractiveLayers}
                  activeInteractiveLayer={activeInteractiveLayer}
                />

                <LayerManager map={map} layers={layerManagerLayers} bounds={layerManagerBounds} />

                {MAP_SHOW_DISCLAIMER && (
                  <MapDisclaimer show={showDisclaimer} setShow={setShowDisclaimer} />
                )}

                <ZoomControlPopup
                  onZoomChange={this.onZoomChange}
                  layers={layerManagerLayers}
                  viewport={viewport}
                />

                <Editor
                  ref={(_) => (this._editorRef = _)}
                  style={{ width: '100%', height: '100%' }}
                  clickRadius={12}
                  mode={this.state.mode}
                  onSelect={this._onSelect}
                  onUpdate={this._onUpdate}
                  editHandleShape={'circle'}
                  featureStyle={getFeatureStyle}
                  editHandleStyle={getEditHandleStyle}
                />

                {this._renderDrawTools()}
                {this._renderControlPanel()}

                {/* SCALE INDICATOR - needs to have access to the map context */}
                <ScaleControl maxWidth={80} style={{ width: '100%', height: '100%' }} />

                <div className="marapp-qa-io-attribution">
                  {POWERED_BY_PC_URL ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('Powered by (two)', {
                          link1: POWERED_BY_URL,
                          value1: 'Impact Observatory',
                          link2: POWERED_BY_PC_URL,
                          value2: t('Planetary Computer'),
                        }),
                      }}
                    />
                  ) : (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('Powered by', {
                          link: POWERED_BY_URL,
                          value: 'Impact Observatory',
                        }),
                      }}
                    />
                  )}
                  {' | '}
                  <span className={classes.sourcesLink} onClick={this._toggleSourcesPopup}>
                    {t('Sources')}
                  </span>
                  {' | '}
                  {APP_TERMS_OF_USE ? (
                    <>
                      <a href={APP_TERMS_OF_USE} target="_blank">
                        {t('Terms of use')}
                      </a>
                      {' | '}
                    </>
                  ) : null}
                  {MAP_SHOW_DISCLAIMER ? (
                    <>
                      <a
                        onClick={() => {
                          setShowDisclaimer(true);
                        }}
                      >
                        {t('Map Disclaimer')}
                      </a>
                      {' | '}
                    </>
                  ) : null}
                  {COMPANY_ABOUT_URL ? (
                    <a href={COMPANY_ABOUT_URL} target="_blank">
                      {t('About')}
                    </a>
                  ) : null}

                  <Spinner
                    size="pico"
                    color="secondary"
                    className={`${classes.spinner} map-load-indicator`}
                  />

                  {/* Sources Popup */}
                  {this.state.showSourcesPopup && (
                    <div ref={this.sourcesPopupRef} className={classes.sourcesPopup}>
                      <div className={classes.sourcesPopupHeader}>
                        <span>{t('Map Sources')}</span>
                        <span
                          className={classes.sourcesPopupClose}
                          onClick={this._closeSourcesPopup}
                        >
                          ✕
                        </span>
                      </div>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: this.state.attributionText || 'Loading sources...',
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        </Map>

        <Legend
          popup={popup}
          layerGroups={layerGroups}
          updateUrlWithLayers={true} // Enable URL updates
          router={router}
        />

        <MapControls>
          <CustomMapControls />
          <div>
            <RecenterControl onClick={this.onRecenterChange} />
            <ZoomControl viewport={viewport} onClick={this.onZoomChange} />
          </div>
        </MapControls>
      </div>
    );
  }

  private handleLoadIndicator(e) {
    document
      .querySelector('.map-load-indicator')
      ?.classList?.[e.type === 'idle' ? 'add' : 'remove']('hidden');

    // hide redundant links, have to do this after baselayer load, can't do nth-of-type with subselector in pure css
    document.querySelectorAll('a[title="Mapbox"]').forEach((elem, key) => {
      if (key > 0) {
        elem.classList.add('hidden');
      }
    });
  }

  _renderDrawTools = () => {
    const { classes } = this.props;
    const { mode } = this.state;
    const isDrawMode = mode instanceof DrawPolygonMode;
    const isEditMode = mode instanceof EditingMode;

    return (
      <div className="custom-draw-tools">
        <div
          className="mapboxgl-ctrl-group mapboxgl-ctrl"
          style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <Tooltip title="Polygon tool (p)" placement="bottom">
            <IconButton
              size="small"
              onClick={() => this.setState({ mode: isDrawMode ? null : new DrawPolygonMode() })}
              style={{
                backgroundColor: '#455A64',
                color: isDrawMode ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.6)',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                boxShadow: isDrawMode
                  ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                  : '0 2px 4px rgba(0, 0, 0, 0.3)',
                transition: 'color 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              <CreateOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Select polygon" placement="bottom">
            <IconButton
              size="small"
              onClick={() => this.setState({ mode: isEditMode ? null : new EditingMode() })}
              style={{
                backgroundColor: '#455A64',
                color: isEditMode ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.6)',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                boxShadow: isEditMode
                  ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                  : '0 2px 4px rgba(0, 0, 0, 0.3)',
                transition: 'color 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              <EditLocationOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete" placement="bottom">
            <IconButton
              size="small"
              onClick={this._onDelete}
              style={{
                backgroundColor: '#455A64',
                color: 'rgba(255,255,255,0.6)',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                transition: 'color 0.2s ease',
              }}
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    );
  };

  _renderControlPanel = () => {
    const { lngLat } = this.state;

    // Safely get polygon data from editor
    let polygon = null;
    try {
      if (this._editorRef) {
        const features = this._editorRef.getFeatures();

        if (features && Array.isArray(features) && features.length > 0) {
          const validPolygons = features.filter(
            (feature) =>
              feature &&
              feature.geometry &&
              (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') &&
              feature.geometry.coordinates &&
              feature.geometry.coordinates.length > 0
          );

          if (validPolygons.length > 0) {
            polygon = {
              type: 'FeatureCollection',
              features: validPolygons.map((feature) => ({
                type: 'Feature',
                properties: feature.properties || {},
                geometry: {
                  type: feature.geometry.type,
                  coordinates: feature.geometry.coordinates,
                },
              })),
            };
          }
        }
      }
    } catch (error) {
      console.error('Error getting features from editor:', error);
      polygon = null;
    }

    return <ControlPanel containerComponent={this.state} lngLat={lngLat} polygon={polygon} />;
  };

  // Helper method to check if drag contains files
  private isDragWithFiles = (e: React.DragEvent): boolean => {
    // Check if there are actual files being dragged
    return e.dataTransfer.files.length > 0 || e.dataTransfer.types.includes('Files');
  };

  // Helper method to check if drag is from legend
  private isDragFromLegend = (e: React.DragEvent): boolean => {
    // Check if this is a legend layer drag
    return e.dataTransfer.types.includes('text/plain') && !e.dataTransfer.types.includes('Files');
  };
}

// TODO Remove UserMenuWrapper after refactoring MapComponent to be a functional component
// This only exists to make use of 'useContext()' inside of it
function UserMenuWrapper(props) {
  const { selected } = props;
  const {
    logout,
    login,
    isAuthenticated,
    userData,
    groups,
    roles,
    selectedGroup,
    setupUserOrg, // This is the setSelectedGroup function from Auth0Context
  } = useContext(Auth0Context);

  const handleWorkspaceChange = (workspace: string) => {
    console.log('[map] org workspace changed to:', [workspace]);

    // Update the selected group in the Auth0Context
    // This will trigger all the useEffect hooks in the Auth0Provider
    // and update userData.selectedGroups and userData.organization
    setupUserOrg([workspace]);
  };

  return (
    <UserMenu
      selected={selected}
      isAuthenticated={isAuthenticated}
      userData={userData}
      groups={groups}
      roles={roles}
      selectedWorkspace={[selectedGroup?.[0]]}
      availableGroups={groups?.map((group) => ({ name: group }))}
      onWorkspaceChange={handleWorkspaceChange} // Use the proper handler instead of just console.log
      MAP_ADMIN_URL={MAP_ADMIN_URL}
      checkAdminRole={checkAdminRole}
      profileLinkProps={{
        component: Link,
        to: { type: 'PROFILE' },
      }}
      onLogin={() => {
        trackGtagEvent('login');
        login();
      }}
      onLogout={logout}
      onSignUp={() => {
        trackGtagEvent('sign_up');
        login({ initialScreen: 'signUp' });
      }}
      onSubmitFeedback={async (values) => {
        return ProfileService.sendFeedback(values);
      }}
    />
  );
}

export default withStyles(styles)(MapComponent);
